import React from "react";

import { Clear, ExpandMore, PersonAddAlt1 } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Trans, useTranslation } from "react-i18next";

import { formatPhoneNumber } from "@/common/utils";
import { BulletinTextField } from "@/components/alerts/add-v2/fields/BulletinTextField";
import { ContactRow } from "@/components/alerts/add-v2/fields/ContactRow";
import { SPLIT_ROW_SX } from "@/components/alerts/add-v2/fields/rowLayout";
import {
  isContactRowEmpty,
  normalizeContactRows,
  validateContactRow,
} from "@/components/alerts/add-v2/model/contactRows";
import { createContact } from "@/components/alerts/add-v2/model/defaults";
import { BulletinContact } from "@/components/alerts/add-v2/model/types";
import { useBulletinField } from "@/components/alerts/add-v2/state/bulletinFormContext";
import { useFinalizeAttempted } from "@/components/alerts/add-v2/validation/finalizeAttempt";
import { ALERT_CLASSIFICATION } from "@/components/alerts/constants";
import { AuthStateContext } from "@/contexts/AuthContext";

/**
 * Classification, point of contact, approval, and the Advanced issued date/time — in that order,
 * which is the prototype's and not the old flow's.
 *
 * Each of the four blocks subscribes to its own field, so typing a phone number does not re-render
 * the classification caption (README decision 8).
 */

/**
 * Same treatment as production: a required select whose caption explains the chosen level and links
 * out to the TBS policy.
 *
 * The options and the caption come from the shared `bulletin/classification` namespace rather than
 * from `bulletin/create-v2`. The prototype paraphrases these three definitions, but the design review
 * did not ask for the copy to change — its note is "Classification still defaults to Protected A" —
 * and production's wording is the TBS-derived one, already translated in both languages.
 *
 * `Trans` rather than production's `dangerouslySetInnerHTML`: two of the three strings emphasise the
 * severity with `<b>`, which is the whole reason that call exists.
 */
const ClassificationField: React.FC = () => {
  const { t } = useTranslation([
    "bulletin/create-v2",
    "bulletin/classification",
  ]);
  const [classification] = useBulletinField("classification");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <BulletinTextField
        name="classification"
        select
        size="small"
        required
        label={t("fields.classification")}
      >
        {Object.values(ALERT_CLASSIFICATION).map((value) => (
          <MenuItem key={value} value={value}>
            {t(`bulletin/classification:${value}`)}
          </MenuItem>
        ))}
      </BulletinTextField>

      {classification && (
        <Typography variant="caption" color="text.secondary" lineHeight={1.45}>
          <Trans
            t={t}
            i18nKey={`bulletin/classification:${classification}_description`}
            components={{ b: <b /> }}
          />{" "}
          <Link
            href="https://www.tbs-sct.canada.ca/pol/doc-eng.aspx?id=32614"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("fields.read_more_here")}
          </Link>
        </Typography>
      )}
    </Box>
  );
};

/**
 * The contact rows, which manage themselves: there is no "Add another contact", because a filled last
 * row grows the list and an emptied row leaves it. `model/contactRows.ts` holds the rule.
 *
 * "Add me" fills the first empty row from the signed-in officer, and disappears once that officer is
 * already on the list.
 */
const ContactRowsField: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const { currentUser } = React.useContext(AuthStateContext);
  const [contacts, setContacts] = useBulletinField("contacts");
  const finalizeAttempted = useFinalizeAttempted();

  const writeRows = (next: BulletinContact[]) =>
    setContacts(normalizeContactRows(next));

  const alreadyOnList =
    !!currentUser?.email &&
    contacts.some((contact) => contact.email === currentUser.email);

  const addMe = () => {
    if (!currentUser) return;

    const me: Omit<BulletinContact, "id"> = {
      name: `${currentUser.first_name ?? ""} ${currentUser.last_name ?? ""}`.trim(),
      email: currentUser.email ?? "",
      phoneNumber: formatPhoneNumber(currentUser.phone_number ?? ""),
      extension: "",
      badgeNumber: currentUser.badge_number ?? "",
    };

    const target = contacts.findIndex(isContactRowEmpty);
    writeRows(
      target === -1
        ? [...contacts, { ...createContact(), ...me }]
        : contacts.map((row, index) =>
            index === target ? { ...row, ...me } : row,
          ),
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.25,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {t("fields.point_of_contact")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("fields.point_of_contact_hint")}
          </Typography>
        </Box>
        {!alreadyOnList && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddAlt1 />}
            disabled={!currentUser}
            onClick={addMe}
            sx={{ borderRadius: "16px", flexShrink: 0 }}
          >
            {t("fields.add_me")}
          </Button>
        )}
      </Box>

      {contacts.map((contact, index) => (
        <React.Fragment key={contact.id}>
          {index > 0 && <Divider />}
          <ContactRow
            value={contact}
            index={index + 1}
            onChange={(next) =>
              writeRows(
                contacts.map((row) => (row.id === next.id ? next : row)),
              )
            }
            // The trailing blank row is the list's own, not the officer's — there is nothing in it
            // to remove. Disabled rather than hidden, which is what this directory's other row lists
            // do (README decision 22).
            removable={index < contacts.length - 1}
            onRemove={() =>
              writeRows(contacts.filter((row) => row.id !== contact.id))
            }
            // Same rule as the incident and location rows: a row says what is missing once it holds
            // something, or once Finalize has been tried.
            errors={
              finalizeAttempted || !isContactRowEmpty(contact)
                ? validateContactRow(contact, index, contacts)
                : {}
            }
          />
        </React.Fragment>
      ))}
    </Box>
  );
};

const ApprovedByField: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" fontWeight={700}>
        {t("fields.approved_by")}
      </Typography>
      <Box sx={SPLIT_ROW_SX}>
        <BulletinTextField
          name="approvedBy"
          size="small"
          label={t("fields.name")}
          placeholder={t("fields.approved_by_placeholder")}
        />
        <BulletinTextField
          name="approvedByBadge"
          size="small"
          label={t("fields.badge_number")}
        />
      </Box>
    </Box>
  );
};

/**
 * The issued date/time override, behind Advanced because a bulletin is issued when it is finalized —
 * this is only for one that was issued earlier, elsewhere.
 *
 * Format matches `fields/IncidentTimeRow` and the production picker, per the 2026-07-28 review. Two
 * differences from production: the box starts open when a value is already loaded, and clearing the
 * input through the keyboard writes `null` — production guards its `onChange` with `if (newValue)`,
 * so there the field can only be cleared with the X.
 */
const IssuedTimeField: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const [issuedTime, setIssuedTime] = useBulletinField("issuedTime");

  return (
    <Accordion
      disableGutters
      elevation={0}
      variant="outlined"
      defaultExpanded={issuedTime !== null}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="body2" fontWeight={700}>
          {t("fields.advanced")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("fields.issued_date_time_description")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DateTimePicker
            label={t("fields.issued_date_time")}
            format="YYYY-MMM-DD hh:mm A"
            ampm
            value={issuedTime}
            disableFuture
            onChange={(value) => setIssuedTime(value)}
            slotProps={{
              textField: { size: "small", sx: { flex: 1, minWidth: 0 } },
            }}
          />
          {issuedTime !== null && (
            <Tooltip title={t("fields.clear_issued_date_time")} placement="top">
              <IconButton
                aria-label={t("fields.clear_issued_date_time")}
                onClick={() => setIssuedTime(null)}
                sx={{ flexShrink: 0 }}
              >
                <Clear />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const ContactsSection: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
    <ClassificationField />
    <ContactRowsField />
    <ApprovedByField />
    <IssuedTimeField />
  </Box>
);
